import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Loader2, ImagePlus, X, Trash2 } from "lucide-react";
import PasswordConfirmDialog from "../components/PasswordConfirmDialog";

export default function Settings() {
  const { user } = useAuth();
  const { reload } = useCurrency();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    whatsapp_number: "",
    exchange_rate_cve_per_eur: 100,
    hero_image_base64: "",
    hero_title: "",
    hero_subtitle: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setForm({
          whatsapp_number: data.whatsapp_number || "",
          exchange_rate_cve_per_eur: data.exchange_rate_cve_per_eur || 100,
          hero_image_base64: data.hero_image_base64 || "",
          hero_title: data.hero_title || "",
          hero_subtitle: data.hero_subtitle || "",
        });
      } catch {
        toast.error("Erro ao carregar definições");
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, hero_image_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.whatsapp_number.trim()) { toast.error("Nº WhatsApp é obrigatório"); return; }
    if (!(Number(form.exchange_rate_cve_per_eur) > 0)) { toast.error("Taxa de câmbio inválida"); return; }
    setSaving(true);
    try {
      await api.put("/settings", {
        whatsapp_number: form.whatsapp_number.trim(),
        exchange_rate_cve_per_eur: Number(form.exchange_rate_cve_per_eur),
        hero_image_base64: form.hero_image_base64 || "",
        hero_title: form.hero_title || "",
        hero_subtitle: form.hero_subtitle || "",
      });
      await reload();
      toast.success("Definições guardadas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <header className="sticky top-0 z-30 glass-nav border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <span className="font-display text-xl">Ladybeauty</span>
            <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="lb-fade-in">
          <div className="lb-overline">Definições</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Personalização da loja</h1>
          <p className="text-sm text-muted-foreground mt-1">Trocar a imagem do hero, o nº WhatsApp e a taxa de câmbio.</p>
        </div>

        {loading ? (
          <div className="h-64 bg-white rounded-2xl lb-shadow-card animate-pulse" />
        ) : (
          <form onSubmit={submit} className="space-y-6">
            {/* Hero image */}
            <section className="bg-white rounded-2xl lb-shadow-card p-5 sm:p-6 space-y-4">
              <div>
                <div className="lb-overline">Loja pública</div>
                <h2 className="font-display text-xl mt-1">Imagem principal (hero)</h2>
                <p className="text-xs text-muted-foreground mt-1">Recomendado: proporção quadrada ou retrato, mín 800×1000px, máx 3MB.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 items-start">
                <div className="aspect-[4/5] rounded-xl overflow-hidden bg-secondary flex items-center justify-center relative">
                  {form.hero_image_base64 ? (
                    <img src={form.hero_image_base64} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center text-muted-foreground p-6">
                      <ImagePlus className="w-8 h-8 mx-auto opacity-60" />
                      <p className="text-xs mt-2">Sem imagem personalizada — a loja mostra a imagem por defeito.</p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <label className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-md cursor-pointer bg-white hover:bg-secondary text-sm font-semibold" data-testid="hero-upload-label">
                    <ImagePlus className="w-4 h-4" /> Escolher imagem
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} data-testid="hero-image-input" />
                  </label>
                  {form.hero_image_base64 && (
                    <Button type="button" variant="ghost" onClick={() => setForm((f) => ({ ...f, hero_image_base64: "" }))} className="text-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]" data-testid="hero-remove-btn">
                      <X className="w-4 h-4 mr-1" /> Remover imagem
                    </Button>
                  )}

                  <div className="space-y-2 pt-2">
                    <Label>Título opcional (deixa vazio para usar o padrão)</Label>
                    <Input value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} placeholder="Ex: Novidades O Boticário" data-testid="hero-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtítulo opcional</Label>
                    <Textarea value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} rows={2} placeholder="Ex: Perfumes e cuidados que apaixonam." data-testid="hero-subtitle-input" />
                  </div>
                </div>
              </div>
            </section>

            {/* WhatsApp + Rate */}
            <section className="bg-white rounded-2xl lb-shadow-card p-5 sm:p-6 space-y-4">
              <div>
                <div className="lb-overline">Encomendas & moeda</div>
                <h2 className="font-display text-xl mt-1">WhatsApp e taxa de câmbio</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nº WhatsApp (formato internacional)</Label>
                  <Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="+351916389631" data-testid="whatsapp-input" />
                  <p className="text-xs text-muted-foreground">Ex: +238XXXXXXX ou +351916389631</p>
                </div>
                <div className="space-y-2">
                  <Label>1 € = ? CVE</Label>
                  <Input type="number" step="0.01" value={form.exchange_rate_cve_per_eur} onChange={(e) => setForm({ ...form, exchange_rate_cve_per_eur: e.target.value })} data-testid="exchange-rate-input" />
                  <p className="text-xs text-muted-foreground">Valor atual usado no seletor CVE ⇄ €.</p>
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate("/dashboard")}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white" data-testid="save-settings-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
              </Button>
            </div>

            {/* Danger zone */}
            <DangerZone />
          </form>
        )}
      </main>
    </div>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);

  return (
    <section className="bg-white rounded-2xl lb-shadow-card p-5 sm:p-6 border border-[hsl(var(--destructive))]/30" data-testid="danger-zone">
      <div>
        <div className="lb-overline text-[hsl(var(--destructive))]">Zona de risco</div>
        <h2 className="font-display text-xl mt-1">Eliminar todos os dados</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Apaga produtos, vendas, fiados, encomendas, lista de compras, categorias, despesas e quebras.
          <br />As tuas contas de acesso e as definições (WhatsApp, taxa câmbio) ficam intactas.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] text-sm font-semibold hover:bg-[hsl(var(--destructive))] hover:text-white transition-colors"
        data-testid="open-wipe-btn"
      >
        <Trash2 className="w-4 h-4" /> Eliminar TODOS os dados
      </button>

      <PasswordConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Eliminar todos os dados"
        description="Esta ação é irreversível. Escreve a tua password para confirmar."
        actionLabel="Sim, eliminar tudo"
        onConfirm={async (password) => {
          const { data } = await api.post("/admin/wipe", { password });
          const summary = Object.entries(data.deleted || {}).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(", ") || "nada para eliminar";
          toast.success(`Eliminado: ${summary}`);
        }}
      />
    </section>
  );
}
